import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Table,
  Badge,
  Collapse,
  Input,
} from 'reactstrap';
import { SkeletonTable } from '../../../../../../../../Components/Common/Skeleton';
import { displayValue as displayParcelValue } from '../../../../../../../../Components/QuantityTable/utils/quantityDeliveryUtils';
import { getAuthToken } from '../../../../../../../../auth/tokenvalidation';
import useSubmittedBidder from '../../../../../../hooks/useSubmittedBidder';
import {
  buildLpgRequirementsFromParcel,
  deliveryDetailRowMatchesBidder,
  displayValue,
  enrichRowsPerBidder,
  mergeBidderDeliveryDetail,
  filterImprovementOfferRows,
  flattenSubmittedBidderDeliveryDetails,
  getRequirementIncotermRouteLabel,
  getOfferRowImprovementStatusLabel,
  offerRowMatchesRequirement,
  isHpsAvailable,
} from '../../../../../../utils/tenderEvaluationHelpers';
import {
  getProcurementProductNameId,
  getProcurementProductTypeNames,
  hasMultipleProductTypes,
  requiresLpgCargoRatioForCargo,
  usesDyedUndyedAlpha,
} from '../../../../../../../../shared/offer-domain/constants';
import {
  getAlphaImprovementFieldValue,
  isOfferRowImprovement,
  isImprovementClosingActive,
  resolveImprovementClosingDate,
} from '../../../../../../../../shared/offer-domain/improvement';
import {
  collectImprovementSelections,
  collectNegotiationSelections,
  getPreselectedRowKeys,
  hasNegotiationOfferId,
} from '../../../../../../../../shared/offer-domain/negotiation';
import { OperationalToleranceSectionValue } from '../../../../../../../../shared/offer-domain/ui';
import { withBidderCounter } from '../../../../../../../../shared/offer-domain/counter';
import AlphaImprovementEditableCells from '../shared/AlphaImprovementEditableCells';
import {
  buildHpsCatalog,
  getHpsAlphaValues,
  resolveMatchingHpsKey,
  MatchingHpsBadge,
  HpsAlphaCell,
  AlphaPriceStacked,
} from '../shared/hpsMatching';
import {
  OFFER_HEADER,
  OFFER_TABLE_STYLE,
  ThLabel,
  thStyle,
} from '../shared/offerTableLayout';
import {
  OfferSelectAllBar,
  OfferStatusBadges,
  OfferStatusFilter,
  filterRowsByOfferStatus,
} from '../shared/offerStatus';
import {
  formatIncotermRoute,
  formatParcelSize,
  formatPortDateRange,
  formatPortLocation,
  isAlphaFilled,
  coalesceAlphaPrice,
} from '../../../../../../utils/deliveryDetailUtils';
import {
  enrichLpgDeliveryItemsWithCargoRatio,
  enrichDeliveryItemsWithCounterOffers,
  enrichDeliveryItemsWithFobLocations,
  isCounterDateChanged,
  isCounterValueChanged,
  FobLocationLabel,
  ParcelDeliveryPricingTabs,
} from '../../../../../../../../shared/offer-domain/delivery';
import { CARGO_RATIO_LABELS } from '../../../../../../../../shared/offer-domain/cargoRatio';
import {
  COUNTER_ACCENT,
  COUNTER_HIGHLIGHT_BG,
} from '../../../../../../../../shared/offer-domain/constants';
import ModalOffer from '../../ModalOffer';
import ActionButton from '../../../../../../../../Components/Common/ActionButton';

const LPG_UNIT = 'MT';
const ALPHA_UNIT = 'USD/MT';

const counterValueStyle = {
  backgroundColor: COUNTER_HIGHLIGHT_BG,
  color: COUNTER_ACCENT,
  fontWeight: 600,
};

const isPortFilled = port => {
  const text = String(port ?? '').trim();
  return Boolean(text) && text !== '-';
};

const PortSummary = ({
  label,
  port,
  parcelSizes = [],
  dateRange,
  showParcelSize = true,
}) => {
  const sizes = isPortFilled(port)
    ? (Array.isArray(parcelSizes) ? parcelSizes : [parcelSizes]).filter(Boolean)
    : [];

  return (
    <Col md={3}>
      <div className='text-muted fw-semibold fs-12 mb-2'>{label}</div>
      <div className='fs-13'>
        <span className='text-muted d-block fs-12'>Port :</span>
        <span className='text-dark d-block mb-2'>{displayValue(port)}</span>
        {showParcelSize && (
          <>
            <span className='text-muted d-block fs-12'>Parcel Size :</span>
            <span className='text-dark d-block mb-2'>
              {sizes.length ? sizes.join(', ') : '-'}
            </span>
          </>
        )}
        <span className='text-muted d-block fs-12'>Date :</span>
        <span className='text-dark d-block'>{dateRange ? dateRange : '-'}</span>
      </div>
    </Col>
  );
};

const formatTimelineRange = (timeline, portNumber = 1, fallback = null) => {
  if (timeline) {
    const startKey = portNumber === 1 ? 'startDate' : `startDate${portNumber}`;
    const endKey = portNumber === 1 ? 'endDate' : `endDate${portNumber}`;
    const start = timeline[startKey];
    const end = timeline[endKey];
    if (start || end) {
      if (start && end) return `${start} - ${end}`;
      return start || end;
    }
  }
  const fallbackText = String(fallback ?? '').trim();
  if (fallbackText && fallbackText !== '-') return fallbackText;
  return null;
};

const getProductLabel = procurement => {
  const productName =
    procurement?.productType?.product?.name ??
    procurement?.productType?.Product?.Name;
  const productDesc = hasMultipleProductTypes(procurement)
    ? getProcurementProductTypeNames(procurement)
    : (procurement?.productType?.description ??
      procurement?.productType?.Description);

  if (productName && productDesc) return `${productName} - ${productDesc}`;
  return productName || productDesc || '-';
};

const getQuantityLabel = parcel => {
  const quantities =
    parcel?.quantities ?? parcel?.listQuantityInformation ?? [];
  if (!quantities.length) return '-';
  return quantities
    .map(item => `${displayParcelValue(item.quantity)} ${LPG_UNIT}`)
    .join(', ');
};

const resolveCargoRatioLabel = row => {
  if (row?.cargoRatioLabel) return row.cargoRatioLabel;
  if (row?.ratioLpg && typeof row.ratioLpg === 'string') return row.ratioLpg;
  if (row?.ratioLpg?.ratioName) return row.ratioLpg.ratioName;
  if (row?.ratioLpg?.name) return row.ratioLpg.name;

  const ratioId = Number(row?.cargoRatio);
  if (Number.isFinite(ratioId) && ratioId >= 1) {
    return CARGO_RATIO_LABELS[ratioId - 1] ?? String(row.cargoRatio);
  }

  return null;
};

const resolveParcelLabel = row => {
  if (row?.parcelSizeLabel) return row.parcelSizeLabel;
  return formatParcelSize(row?.parcelSize ?? row?.deliveryBasis, LPG_UNIT);
};

const collectParcelSizeLabels = (parcel, rows = []) => {
  const seen = new Set();
  const labels = [];

  const pushLabel = label => {
    if (!label || label.startsWith('-') || seen.has(label)) return;
    seen.add(label);
    labels.push(label);
  };

  rows.forEach(row => {
    pushLabel(resolveParcelLabel(row));
  });
  if (labels.length) return labels;

  const quantities =
    parcel?.quantities ?? parcel?.listQuantityInformation ?? [];
  quantities.forEach(item => {
    pushLabel(formatParcelSize(item?.quantity, LPG_UNIT));
  });
  if (labels.length) return labels;

  const fromDelivery = rows.find(
    row => Array.isArray(row?.parcelSizeLabels) && row.parcelSizeLabels.length
  )?.parcelSizeLabels;
  (fromDelivery || []).forEach(pushLabel);

  return labels;
};

const renderLpgPortBlock = (
  port,
  parcelSizes = [],
  {
    startDate,
    endDate,
    fallbackDate,
    highlight = {},
    showParcelSize = true,
    locationNames = null,
    route = null,
  } = {}
) => {
  const dateText = formatPortDateRange(startDate, endDate, fallbackDate);
  const sizes = isPortFilled(port)
    ? (parcelSizes || []).filter(size => {
        const text = String(size ?? '').trim();
        return text && text !== '-' && !text.startsWith('-');
      })
    : [];
  const highlightPort = highlight === true || highlight?.port;
  const highlightParcel = highlight === true || highlight?.parcelSize;
  const highlightDate = highlight === true || highlight?.date;
  const hasLocationNames =
    Array.isArray(locationNames) && locationNames.length > 0;

  return (
    <div className='fs-12 lh-base d-flex flex-column gap-1'>
      <span>
        <span className='text-muted'>Port : </span>
        <span
          className={highlightPort ? 'fw-semibold' : 'text-dark'}
          style={
            highlightPort
              ? {
                  ...counterValueStyle,
                  padding: '0 4px',
                  borderRadius: 2,
                  display: 'inline-block',
                }
              : undefined
          }
        >
          {hasLocationNames ? (
            <FobLocationLabel
              names={locationNames}
              fallback={port}
              route={route}
            />
          ) : (
            formatPortLocation(port)
          )}
        </span>
      </span>
      {showParcelSize && (
        <span>
          <span className='text-muted'>Parcel Size : </span>
          <span
            className={highlightParcel ? 'fw-semibold' : 'text-dark'}
            style={
              highlightParcel
                ? {
                    ...counterValueStyle,
                    padding: '0 4px',
                    borderRadius: 2,
                    display: 'inline-block',
                  }
                : undefined
            }
          >
            {sizes.length ? sizes.join(', ') : '-'}
          </span>
        </span>
      )}
      <span>
        <span className='text-muted'>Date : </span>
        <span
          className={highlightDate ? 'fw-semibold' : 'text-dark'}
          style={
            highlightDate
              ? {
                  ...counterValueStyle,
                  padding: '0 4px',
                  borderRadius: 2,
                  display: 'inline-block',
                }
              : undefined
          }
        >
          {dateText}
        </span>
      </span>
    </div>
  );
};

const getBidderRequirementRows = ({
  bidder,
  requirement,
  parcelId,
  deliveryDetails,
}) =>
  (deliveryDetails ?? []).filter(
    row =>
      deliveryDetailRowMatchesBidder(row, bidder) &&
      offerRowMatchesRequirement(row, requirement, parcelId)
  );

const getOfferRowKey = (parcelId, requirementIndex, bidder, detailRow, kind) =>
  `${parcelId ?? 'parcel'}-${requirementIndex}-${bidder?.id ?? bidder?.idOffer}-${detailRow?.quantityInformationDetailId ?? detailRow?.id ?? 'x'}-${detailRow?.cargoRatio ?? ''}-${resolveParcelLabel(detailRow)}-${kind}`;

const getOfferSelectionKey = (parcelId, requirementIndex, bidder, kind) =>
  `${parcelId ?? 'parcel'}-${requirementIndex}-${bidder?.id ?? bidder?.idOffer}-${kind}`;

const getCompliantLpgRows = ({
  bidders,
  requirement,
  requirementIndex,
  parcelId,
  deliveryDetails,
}) => {
  if (!bidders?.length) return [];

  return bidders.flatMap(bidder => {
    const matchedRows = getBidderRequirementRows({
      bidder,
      requirement,
      parcelId,
      deliveryDetails,
    });

    const offerRows = matchedRows
      .filter(row => isAlphaFilled(row?.alpha))
      .map(detailRow => ({
        bidder,
        detailRow,
        kind: 'offer',
        selectionKey: getOfferSelectionKey(
          parcelId,
          requirementIndex,
          bidder,
          'offer'
        ),
        rowKey: getOfferRowKey(
          parcelId,
          requirementIndex,
          bidder,
          detailRow,
          'offer'
        ),
      }));

    const counterCandidates = matchedRows.filter(
      row =>
        isAlphaFilled(row?.counterAlpha) ||
        (Array.isArray(row?.counterCargoRatios) &&
          row.counterCargoRatios.some(ratio => isAlphaFilled(ratio?.alpha)))
    );

    /** Counter is defined per parcel size; original ratio rows must not duplicate it. */
    const counterParcelByLabel = new Map();
    counterCandidates.forEach(row => {
      const label = String(resolveParcelLabel(row) ?? '');
      if (!label) return;
      const current = counterParcelByLabel.get(label);
      if (
        !current ||
        (!current.quantityInformationDetailCounterId &&
          row.quantityInformationDetailCounterId)
      ) {
        counterParcelByLabel.set(label, row);
      }
    });

    const counterRatios = counterCandidates.find(
      row =>
        Array.isArray(row?.counterCargoRatios) && row.counterCargoRatios.length
    )?.counterCargoRatios ?? [{ id: null, label: null }];

    const counterRows = [];
    counterRatios.forEach(ratio => {
      counterParcelByLabel.forEach(detailRow => {
        counterRows.push({
          bidder,
          detailRow,
          counterRatio: ratio,
          kind: 'counter',
          selectionKey: getOfferSelectionKey(
            parcelId,
            requirementIndex,
            bidder,
            'counter'
          ),
          rowKey: `${getOfferRowKey(
            parcelId,
            requirementIndex,
            bidder,
            detailRow,
            'counter'
          )}-${ratio?.id ?? 'none'}`,
        });
      });
    });

    return [...offerRows, ...counterRows];
  });
};

const getRowCargoRatioLabel = row =>
  row?.kind === 'counter'
    ? (row?.counterRatio?.label ?? null)
    : resolveCargoRatioLabel(row?.detailRow);

const getCargoRatioSortKey = row => String(getRowCargoRatioLabel(row) ?? '-');

const getParcelSizeSortValue = detailRow => {
  const raw = detailRow?.parcelSize ?? detailRow?.deliveryBasis;
  const numeric = Number(String(raw ?? '').replace(/[^\d.-]/g, ''));
  return Number.isFinite(numeric) ? numeric : 0;
};

const buildGroupedOfferRows = (compliantRows = []) => {
  const groups = [];
  const indexByKey = new Map();

  compliantRows.forEach(row => {
    const groupKey =
      row.selectionKey ??
      `${row.bidder?.id ?? row.bidder?.idOffer}-${row.kind}`;
    if (!indexByKey.has(groupKey)) {
      indexByKey.set(groupKey, groups.length);
      groups.push({
        groupKey,
        bidder: row.bidder,
        kind: row.kind,
        rows: [],
      });
    }
    groups[indexByKey.get(groupKey)].rows.push(row);
  });

  return groups.flatMap(group => {
    const sorted = [...group.rows].sort((a, b) => {
      const ratioCmp = getCargoRatioSortKey(a).localeCompare(
        getCargoRatioSortKey(b)
      );
      if (ratioCmp !== 0) return ratioCmp;
      return (
        getParcelSizeSortValue(a.detailRow) -
        getParcelSizeSortValue(b.detailRow)
      );
    });

    const ratioBuckets = new Map();
    sorted.forEach(row => {
      const key = getCargoRatioSortKey(row);
      if (!ratioBuckets.has(key)) ratioBuckets.set(key, []);
      ratioBuckets.get(key).push(row);
    });

    const result = [];
    let offset = 0;
    const groupRowSpan = sorted.length;

    ratioBuckets.forEach((ratioRows, cargoRatioKey) => {
      ratioRows.forEach((row, sizeIndex) => {
        result.push({
          ...row,
          showGroupCells: offset === 0,
          groupRowSpan,
          showCargoRatioCell: sizeIndex === 0,
          cargoRatioRowSpan: ratioRows.length,
          cargoRatioLabel:
            cargoRatioKey === '-'
              ? null
              : (getRowCargoRatioLabel(row) ?? cargoRatioKey),
          selectionKey: group.groupKey,
        });
        offset += 1;
      });
    });

    return result;
  });
};

const requirementHasOfferData = ({
  requirement,
  requirementIndex,
  parcelId,
  deliveryDetails,
  bidders,
  improvementOnly = false,
}) => {
  const rows = getCompliantLpgRows({
    bidders,
    requirement,
    requirementIndex,
    parcelId,
    deliveryDetails,
  });
  return (improvementOnly ? filterImprovementOfferRows(rows) : rows).length > 0;
};

const OfferDetailRows = ({
  compliantRows,
  isLoading,
  selectedRowKeys,
  onSelectRow,
  requiresCargoRatio,
  parcelSizes = [],
  onViewDetail,
  improvementOnly = false,
  showCheckboxes = false,
  selectionLocked = false,
  negotiationMode = false,
  usesDyedUndyed = false,
  colSpan,
  alphaImprovementEditable = false,
  onAlphaImprovementChange,
  hpsAvailable = false,
  procurement = null,
  hpsCatalog = [],
  showImprovementHpsColumns = false,
  isImprovementClosed = false,
}) => {
  const groupedRows = useMemo(
    () => buildGroupedOfferRows(compliantRows),
    [compliantRows]
  );
  const statusWording = (procurement?.evaluationStatusWording || '')
    .toString()
    .toLowerCase()
    .trim();
  const statusKey = String(procurement?.evaluationStatus ?? '')
    .toLowerCase()
    .trim();
  const showCounterpartyName =
    (improvementOnly || negotiationMode) &&
    (statusWording === 'closing evaluation' ||
      statusKey === 'closingevaluation' ||
      isImprovementClosed);

  if (isLoading) {
    return <SkeletonTable inline rows={3} columns={colSpan} />;
  }

  if (!groupedRows.length) {
    return (
      <tr>
        <td colSpan={colSpan} className='text-center text-muted py-4 fs-13'>
          {improvementOnly
            ? 'Belum ada data improvement.'
            : negotiationMode
              ? 'Tidak ada Offer dengan ID Offer.'
              : 'Belum ada data offer dengan alpha terisi.'}
        </td>
      </tr>
    );
  }

  return groupedRows.map(
    (
      {
        bidder,
        detailRow,
        kind,
        rowKey,
        selectionKey: rowSelectionKey,
        showGroupCells,
        groupRowSpan,
        showCargoRatioCell,
        cargoRatioRowSpan,
        cargoRatioLabel: groupedCargoRatioLabel,
        counterRatio = null,
      },
      index
    ) => {
      const isCounter = kind === 'counter';
      const isPerCargoRatioImprovement =
        improvementOnly &&
        requiresCargoRatio &&
        (isCounter
          ? !!counterRatio?.recordId
          : !!detailRow?.quantityInformationCargoRatioId);
      const improvementDetailRow = counterRatio
        ? {
            ...detailRow,
            counterCargoRatioRecordId:
              counterRatio.recordId ??
              detailRow.counterCargoRatioRecordId ??
              null,
            counterCargoRatioId:
              counterRatio.id ?? detailRow.counterCargoRatioId,
            quantityInformationCargoRatioId:
              counterRatio.quantityInformationCargoRatioFK ??
              detailRow.quantityInformationCargoRatioId,
            cargoRatioParcelSize:
              counterRatio.parcelSize ?? detailRow.cargoRatioParcelSize,
            cargoRatioAlphaImprovement:
              counterRatio.alphaImprovement ??
              counterRatio.cargoRatioAlphaImprovement ??
              detailRow.cargoRatioAlphaImprovement ??
              null,
            improvementAlpha:
              counterRatio.alphaImprovement ??
              detailRow.improvementAlpha ??
              null,
            alphaHps: counterRatio.alphaHps ?? detailRow.alphaHps,
            alphaDyedHps: counterRatio.alphaDyedHps ?? detailRow.alphaDyedHps,
            alphaUndyedHps:
              counterRatio.alphaUndyedHps ?? detailRow.alphaUndyedHps,
            cargoRatioSnapshot:
              counterRatio.cargoRatioSnapshot ??
              detailRow.cargoRatioSnapshot ??
              null,
            counterCargoRatioSnapshot:
              counterRatio.cargoRatioSnapshot ??
              detailRow.counterCargoRatioSnapshot ??
              null,
          }
        : detailRow;
      const alphaValue = isCounter
        ? coalesceAlphaPrice(
            counterRatio?.alpha,
            detailRow.counterAlpha,
            detailRow.alpha2,
            detailRow.alphaMitra2,
            detailRow.alpha,
            detailRow.alphaMitra1,
            detailRow.AlphaMitra1
          )
        : coalesceAlphaPrice(
            detailRow.alpha,
            detailRow.alphaMitra1,
            detailRow.AlphaMitra1,
            detailRow.alpha1
          );
      if (!isAlphaFilled(alphaValue)) return null;

      const parcelLabel = resolveParcelLabel(detailRow);
      const cargoRatioLabel = isCounter
        ? groupedCargoRatioLabel
        : (groupedCargoRatioLabel ?? resolveCargoRatioLabel(detailRow));
      const incoterm = isCounter
        ? (detailRow.counterIncoterm ?? detailRow.incoterm)
        : detailRow.incoterm;
      const route = isCounter
        ? (detailRow.counterRoute ?? detailRow.route)
        : detailRow.route;
      const originalIncotermRoute = formatIncotermRoute(
        detailRow.incoterm,
        detailRow.route
      );
      const counterIncotermRoute = formatIncotermRoute(incoterm, route);
      const incotermRouteChanged =
        isCounter &&
        !!detailRow.counterIncoterm &&
        isCounterValueChanged(originalIncotermRoute, counterIncotermRoute);
      const originalPort1 = detailRow.port1 || detailRow.loadingPort;
      const counterPort1 =
        detailRow.counterLoadingPort ||
        detailRow.port1 ||
        detailRow.loadingPort;
      const port1 = isCounter ? counterPort1 : originalPort1;
      const port2 = isCounter
        ? detailRow.counterPort2 || detailRow.port2
        : detailRow.port2;
      const port3 = isCounter
        ? detailRow.counterPort3 || detailRow.port3
        : detailRow.port3;
      const alphaChanged =
        isCounter && isCounterValueChanged(detailRow.alpha, alphaValue);
      const port1Highlight = isCounter
        ? {
            port: isCounterValueChanged(
              originalPort1,
              detailRow.counterLoadingPort
            ),
            date: isCounterDateChanged(
              detailRow.port1StartDate,
              detailRow.port1EndDate,
              detailRow.counterStartDate,
              detailRow.counterEndDate,
              detailRow.addAld,
              detailRow.counterAddAld
            ),
          }
        : {};
      const port2Highlight = isCounter
        ? {
            port: isCounterValueChanged(
              detailRow.port2,
              detailRow.counterPort2
            ),
            date: isCounterDateChanged(
              detailRow.port2StartDate,
              detailRow.port2EndDate,
              detailRow.counterStartDate2,
              detailRow.counterEndDate2,
              detailRow.addAld2,
              detailRow.counterAddAld2
            ),
          }
        : {};
      const port3Highlight = isCounter
        ? {
            port: isCounterValueChanged(
              detailRow.port3,
              detailRow.counterPort3
            ),
            date: isCounterDateChanged(
              detailRow.port3StartDate,
              detailRow.port3EndDate,
              detailRow.counterStartDate3,
              detailRow.counterEndDate3,
              detailRow.addAld3,
              detailRow.counterAddAld3
            ),
          }
        : {};
      const groupSelected =
        !!rowSelectionKey && selectedRowKeys.has(rowSelectionKey);
      const isImprovement = isOfferRowImprovement({ bidder, detailRow, kind });

      return (
        <tr
          key={rowKey ?? `${bidder.id ?? index}-${index}`}
          className='border-bottom'
        >
          {showGroupCells && (
            <>
              {showCheckboxes && (
                <td rowSpan={groupRowSpan} className='text-center align-top'>
                  <Input
                    type='checkbox'
                    className='form-check-input'
                    checked={groupSelected}
                    disabled={
                      selectionLocked ||
                      (!negotiationMode && isImprovement) ||
                      (negotiationMode &&
                        !hasNegotiationOfferId(bidder?.idOffer))
                    }
                    onChange={e => {
                      if (selectionLocked) return;
                      if (!negotiationMode && isImprovement) return;
                      if (
                        negotiationMode &&
                        !hasNegotiationOfferId(bidder?.idOffer)
                      )
                        return;
                      const checked = e.target.checked;
                      onSelectRow?.(rowSelectionKey, checked);
                    }}
                    aria-label={`Pilih ${displayValue(bidder.idOffer)}`}
                  />
                </td>
              )}
              {showCheckboxes && !negotiationMode && (
                <td
                  rowSpan={groupRowSpan}
                  className='text-dark align-top fs-12'
                >
                  {getOfferRowImprovementStatusLabel({
                    bidder,
                    detailRow,
                    kind,
                  }) === 'Requested' ? (
                    <Badge
                      color='success'
                      className='px-2 py-1 fs-11 fw-medium'
                      style={{ whiteSpace: 'normal', lineHeight: 1.25 }}
                    >
                      Requested
                    </Badge>
                  ) : (
                    <Badge
                      color='secondary'
                      className='px-2 py-1 fs-11 fw-medium'
                      style={{ whiteSpace: 'normal', lineHeight: 1.25 }}
                    >
                      Not Req.
                    </Badge>
                  )}
                </td>
              )}
              <td
                rowSpan={groupRowSpan}
                className={
                  showCounterpartyName || negotiationMode
                    ? 'text-dark align-top'
                    : 'text-dark align-top text-nowrap'
                }
                style={
                  showCounterpartyName || negotiationMode
                    ? {
                        maxWidth: 120,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        lineHeight: 1.25,
                      }
                    : undefined
                }
                title={
                  showCounterpartyName || negotiationMode
                    ? displayValue(bidder?.companyName || bidder?.idOffer)
                    : undefined
                }
              >
                {showCounterpartyName || negotiationMode ? (
                  <>
                    <div className='fw-semibold text-dark'>
                      {displayValue(bidder?.companyName || bidder?.idOffer)}
                    </div>
                    {bidder?.companyName ? (
                      <div className='text-muted fs-12'>
                        {displayValue(bidder.idOffer)}
                      </div>
                    ) : null}
                  </>
                ) : (
                  displayValue(bidder.idOffer)
                )}
              </td>
              <td rowSpan={groupRowSpan} className='align-top'>
                <OfferStatusBadges
                  isCounter={isCounter}
                  isLateSubmission={bidder?.isLateSubmission === true}
                />
              </td>
              <td
                rowSpan={groupRowSpan}
                className='text-dark align-top'
                style={incotermRouteChanged ? counterValueStyle : undefined}
              >
                {formatIncotermRoute(incoterm, route)}
              </td>
              <td rowSpan={groupRowSpan} className='text-dark align-top'>
                <OperationalToleranceSectionValue
                  procurement={withBidderCounter(procurement, bidder)}
                  preferCounter
                />
              </td>
              <td rowSpan={groupRowSpan} className='align-top'>
                {isCounter
                  ? renderLpgPortBlock(port1, parcelSizes, {
                      startDate:
                        detailRow.counterStartDate ?? detailRow.port1StartDate,
                      endDate:
                        detailRow.counterEndDate ?? detailRow.port1EndDate,
                      fallbackDate: detailRow.counterAddAld ?? detailRow.addAld,
                      highlight: port1Highlight,
                      locationNames: detailRow.fobLocationNames,
                      route: detailRow.counterRoute ?? detailRow.route,
                    })
                  : renderLpgPortBlock(port1, parcelSizes, {
                      startDate: detailRow.port1StartDate,
                      endDate: detailRow.port1EndDate,
                      fallbackDate: detailRow.addAld,
                      locationNames: detailRow.fobLocationNames,
                      route: detailRow.route,
                    })}
              </td>
              <td rowSpan={groupRowSpan} className='align-top'>
                {port2
                  ? renderLpgPortBlock(port2, parcelSizes, {
                      startDate: isCounter
                        ? (detailRow.counterStartDate2 ??
                          detailRow.port2StartDate)
                        : detailRow.port2StartDate,
                      endDate: isCounter
                        ? (detailRow.counterEndDate2 ?? detailRow.port2EndDate)
                        : detailRow.port2EndDate,
                      fallbackDate: isCounter
                        ? (detailRow.counterAddAld2 ?? detailRow.addAld2)
                        : detailRow.addAld2,
                      highlight: port2Highlight,
                    })
                  : '-'}
              </td>
              <td rowSpan={groupRowSpan} className='align-top'>
                {port3
                  ? renderLpgPortBlock(port3, parcelSizes, {
                      startDate: isCounter
                        ? (detailRow.counterStartDate3 ??
                          detailRow.port3StartDate)
                        : detailRow.port3StartDate,
                      endDate: isCounter
                        ? (detailRow.counterEndDate3 ?? detailRow.port3EndDate)
                        : detailRow.port3EndDate,
                      fallbackDate: isCounter
                        ? (detailRow.counterAddAld3 ?? detailRow.addAld3)
                        : detailRow.addAld3,
                      highlight: port3Highlight,
                    })
                  : '-'}
              </td>
            </>
          )}
          {requiresCargoRatio && showCargoRatioCell && (
            <td rowSpan={cargoRatioRowSpan} className='text-dark align-top'>
              {cargoRatioLabel ? (
                <span
                  className='badge bg-primary-subtle text-primary border border-primary-subtle fw-medium rounded-2 fs-12 px-2 py-1'
                  style={{ whiteSpace: 'normal', lineHeight: 1.25 }}
                >
                  {cargoRatioLabel}
                </span>
              ) : (
                '-'
              )}
            </td>
          )}
          <td className='text-dark fw-semibold'>{parcelLabel}</td>
          <td
            className='text-dark'
            style={alphaChanged ? counterValueStyle : undefined}
          >
            <AlphaPriceStacked value={alphaValue} suffix={ALPHA_UNIT} />
          </td>
          {negotiationMode && (
            <>
              <td className='text-dark'>
                <HpsAlphaCell
                  usesDyedUndyed={false}
                  hps={getHpsAlphaValues(improvementDetailRow, {
                    isCounter,
                    hpsCatalog,
                  })}
                  suffix={ALPHA_UNIT}
                  hpsAvailable={hpsAvailable}
                />
              </td>
              <td>
                <MatchingHpsBadge
                  matchKey={resolveMatchingHpsKey({
                    usesDyedUndyed: false,
                    offerAlpha: alphaValue,
                    offerDyed: null,
                    offerUndyed: null,
                    hps: getHpsAlphaValues(improvementDetailRow, {
                      isCounter,
                      hpsCatalog,
                    }),
                    statusFromApi:
                      detailRow.matchingHps ??
                      detailRow.hpsMatching ??
                      detailRow.matchingHpsStatus,
                    hpsAvailable,
                  })}
                />
              </td>
            </>
          )}
          {improvementOnly || negotiationMode
            ? (isPerCargoRatioImprovement || showGroupCells) &&
              (alphaImprovementEditable ? (
                <AlphaImprovementEditableCells
                  detailRow={improvementDetailRow}
                  kind={kind}
                  usesDyedUndyed={usesDyedUndyed}
                  pricingUnit={ALPHA_UNIT}
                  onAlphaImprovementChange={onAlphaImprovementChange}
                  rowSpan={
                    isPerCargoRatioImprovement ? undefined : groupRowSpan
                  }
                />
              ) : usesDyedUndyed ? (
                <>
                  <td
                    rowSpan={
                      isPerCargoRatioImprovement ? undefined : groupRowSpan
                    }
                    className='text-dark align-top'
                  >
                    <AlphaPriceStacked
                      value={getAlphaImprovementFieldValue(
                        improvementDetailRow,
                        kind,
                        'dyed'
                      )}
                      suffix={ALPHA_UNIT}
                    />
                  </td>
                  <td
                    rowSpan={
                      isPerCargoRatioImprovement ? undefined : groupRowSpan
                    }
                    className='text-dark align-top'
                  >
                    <AlphaPriceStacked
                      value={getAlphaImprovementFieldValue(
                        improvementDetailRow,
                        kind,
                        'undyed'
                      )}
                      suffix={ALPHA_UNIT}
                    />
                  </td>
                </>
              ) : (
                <td
                  rowSpan={
                    isPerCargoRatioImprovement ? undefined : groupRowSpan
                  }
                  className='text-dark align-top'
                >
                  <AlphaPriceStacked
                    value={getAlphaImprovementFieldValue(
                      improvementDetailRow,
                      kind,
                      'alpha'
                    )}
                    suffix={ALPHA_UNIT}
                  />
                </td>
              ))
            : showGroupCells && (
                <td rowSpan={groupRowSpan} className='text-center align-top'>
                  <ActionButton
                    variant='icon'
                    size='sm'
                    icon='ri-eye-line'
                    label='Detail'
                    onClick={() => onViewDetail?.(bidder, detailRow)}
                  />
                </td>
              )}
          {showImprovementHpsColumns && (
            <>
              <td className='text-dark'>
                <HpsAlphaCell
                  usesDyedUndyed={usesDyedUndyed}
                  hps={getHpsAlphaValues(improvementDetailRow, {
                    isCounter,
                    hpsCatalog,
                  })}
                  suffix={ALPHA_UNIT}
                  hpsAvailable={hpsAvailable}
                />
              </td>
              <td>
                <MatchingHpsBadge
                  matchKey={resolveMatchingHpsKey({
                    usesDyedUndyed,
                    offerAlpha: usesDyedUndyed
                      ? null
                      : getAlphaImprovementFieldValue(
                          improvementDetailRow,
                          kind,
                          'alpha'
                        ),
                    offerDyed: usesDyedUndyed
                      ? getAlphaImprovementFieldValue(
                          improvementDetailRow,
                          kind,
                          'dyed'
                        )
                      : null,
                    offerUndyed: usesDyedUndyed
                      ? getAlphaImprovementFieldValue(
                          improvementDetailRow,
                          kind,
                          'undyed'
                        )
                      : null,
                    hps: getHpsAlphaValues(improvementDetailRow, {
                      isCounter,
                      hpsCatalog,
                    }),
                    statusFromApi:
                      detailRow.matchingHps ??
                      detailRow.hpsMatching ??
                      detailRow.matchingHpsStatus,
                    hpsAvailable,
                  })}
                />
              </td>
            </>
          )}
        </tr>
      );
    }
  );
};

const RequirementCard = ({
  requirement,
  requirementIndex,
  parcelId,
  parcel,
  bidders,
  isLoadingBidders,
  deliveryDetails,
  isLoadingDeliveryDetails,
  procurement,
  requiresCargoRatio,
  onViewDetail,
  improvementOnly = false,
  negotiationMode = false,
  showCheckboxes = false,
  selectionLocked = false,
  selectionKey,
  onRequirementSelectionChange,
  selectionClearToken = 0,
  selectAllToken = 0,
  alphaImprovementEditable = false,
  onAlphaImprovementChange,
  onNegotiationOfferAvailability,
  preselectedOffers = null,
  showImprovementHpsColumns = false,
  isImprovementClosed = false,
}) => {
  const filterImprovement = improvementOnly || negotiationMode;
  const hasData = useMemo(
    () =>
      requirementHasOfferData({
        requirement,
        requirementIndex,
        parcelId,
        deliveryDetails,
        bidders,
        improvementOnly: filterImprovement,
      }),
    [
      requirement,
      requirementIndex,
      parcelId,
      deliveryDetails,
      bidders,
      filterImprovement,
    ]
  );

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (hasData) setIsExpanded(true);
  }, [hasData]);

  const timeline = requirement?.timeline;
  const incotermRouteLabel = getRequirementIncotermRouteLabel(requirement);
  const selectAllRef = useRef(null);
  const usesDyedUndyed = usesDyedUndyedAlpha(
    getProcurementProductNameId(procurement)
  );
  const colSpan =
    (showCheckboxes
      ? requiresCargoRatio
        ? 15
        : 14
      : requiresCargoRatio
        ? usesDyedUndyed
          ? 12
          : 11
        : usesDyedUndyed
          ? 11
          : 10) -
    (!improvementOnly && !negotiationMode ? 2 : 0) +
    (showImprovementHpsColumns ? 2 : 0);

  const compliantRows = useMemo(() => {
    const rows = getCompliantLpgRows({
      bidders,
      requirement,
      requirementIndex,
      parcelId,
      deliveryDetails,
    });
    return filterImprovement ? filterImprovementOfferRows(rows) : rows;
  }, [
    bidders,
    requirement,
    requirementIndex,
    parcelId,
    deliveryDetails,
    filterImprovement,
  ]);

  const hasSelectableOffer = useMemo(
    () =>
      compliantRows.some(row => hasNegotiationOfferId(row?.bidder?.idOffer)),
    [compliantRows]
  );

  useEffect(() => {
    if (!negotiationMode || !onNegotiationOfferAvailability) return;
    if (isLoadingBidders || isLoadingDeliveryDetails) return;
    onNegotiationOfferAvailability({
      parcelId,
      requirementIndex,
      hasSelectableOffer,
    });
  }, [
    negotiationMode,
    onNegotiationOfferAvailability,
    isLoadingBidders,
    isLoadingDeliveryDetails,
    parcelId,
    requirementIndex,
    hasSelectableOffer,
  ]);

  const parcelSizes = useMemo(
    () =>
      collectParcelSizeLabels(
        parcel,
        compliantRows.map(row => row.detailRow)
      ),
    [parcel, compliantRows]
  );

  const hpsCatalog = useMemo(
    () => buildHpsCatalog({ deliveryDetails, requirement }),
    [deliveryDetails, requirement]
  );

  const [statusFilters, setStatusFilters] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState(() => new Set());

  const filteredRows = useMemo(
    () => filterRowsByOfferStatus(compliantRows, statusFilters),
    [compliantRows, statusFilters]
  );

  const selectableKeys = useMemo(
    () => [
      ...new Set(
        filteredRows
          .filter(row => negotiationMode || !isOfferRowImprovement(row))
          .filter(
            row =>
              !negotiationMode || hasNegotiationOfferId(row?.bidder?.idOffer)
          )
          .map(
            row =>
              row.selectionKey ??
              `${row.bidder?.id ?? row.bidder?.idOffer}-${row.kind}`
          )
      ),
    ],
    [filteredRows, negotiationMode]
  );

  useEffect(() => {
    if (!selectionClearToken) return;
    if (improvementOnly || negotiationMode) {
      setSelectedRowKeys(new Set());
      return;
    }
    // Keep Requested rows checked; only clear selectable offers.
    setSelectedRowKeys(
      new Set(
        compliantRows
          .filter(row => isOfferRowImprovement(row))
          .map(
            row =>
              row.selectionKey ??
              `${row.bidder?.id ?? row.bidder?.idOffer}-${row.kind}`
          )
          .filter(Boolean)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionClearToken]);

  useEffect(() => {
    if (selectionLocked) setSelectedRowKeys(new Set());
  }, [selectionLocked]);

  useEffect(() => {
    if (
      improvementOnly ||
      negotiationMode ||
      selectionLocked ||
      !selectAllToken
    )
      return;
    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      compliantRows
        .filter(row => isOfferRowImprovement(row))
        .forEach(row => {
          const key =
            row.selectionKey ??
            `${row.bidder?.id ?? row.bidder?.idOffer}-${row.kind}`;
          if (key) next.add(key);
        });
      selectableKeys.forEach(key => next.add(key));
      return next;
    });
    // Only react to explicit Select all action, not filter/row churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAllToken]);

  useEffect(() => {
    if (improvementOnly) return;
    const validKeys = new Set(
      compliantRows.map(
        row =>
          row.selectionKey ??
          `${row.bidder?.id ?? row.bidder?.idOffer}-${row.kind}`
      )
    );
    setSelectedRowKeys(prev => {
      const next = new Set([...prev].filter(key => validKeys.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [compliantRows, improvementOnly]);

  // Prefill (saved package): check matching rows once, after rows load.
  const preselectAppliedRef = useRef(false);
  useEffect(() => {
    if (preselectAppliedRef.current || !negotiationMode) return;
    if (!preselectedOffers?.length || !compliantRows.length) return;
    preselectAppliedRef.current = true;
    const keys = getPreselectedRowKeys(
      compliantRows,
      preselectedOffers,
      'selectionKey'
    );
    if (keys.length) setSelectedRowKeys(new Set(keys));
  }, [negotiationMode, preselectedOffers, compliantRows]);

  useEffect(() => {
    if (improvementOnly || negotiationMode) return;
    const improvementKeys = compliantRows
      .filter(row => isOfferRowImprovement(row))
      .map(
        row =>
          row.selectionKey ??
          `${row.bidder?.id ?? row.bidder?.idOffer}-${row.kind}`
      )
      .filter(Boolean);
    if (!improvementKeys.length) return;

    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      let changed = false;
      improvementKeys.forEach(key => {
        if (!next.has(key)) {
          next.add(key);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [compliantRows, improvementOnly, negotiationMode]);

  useEffect(() => {
    if (
      !showCheckboxes ||
      !selectionKey ||
      !onRequirementSelectionChange ||
      selectionLocked
    )
      return;
    const collect = negotiationMode
      ? collectNegotiationSelections
      : collectImprovementSelections;
    onRequirementSelectionChange(
      selectionKey,
      collect({
        compliantRows,
        selectedKeys: [...selectedRowKeys],
        parcelId,
        requirementIndex,
        requirement,
        keyField: 'selectionKey',
      })
    );
  }, [
    selectionKey,
    selectedRowKeys,
    compliantRows,
    parcelId,
    requirementIndex,
    requirement,
    onRequirementSelectionChange,
    showCheckboxes,
    negotiationMode,
    selectionLocked,
  ]);

  useEffect(() => {
    if (!selectionLocked || !selectionKey || !onRequirementSelectionChange)
      return;
    onRequirementSelectionChange(selectionKey, []);
  }, [selectionLocked, selectionKey, onRequirementSelectionChange]);

  const allSelected =
    selectableKeys.length > 0 &&
    selectableKeys.every(key => selectedRowKeys.has(key));
  const someSelected = selectableKeys.some(key => selectedRowKeys.has(key));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const handleSelectAll = checked => {
    if (selectionLocked) return;
    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      selectableKeys.forEach(key => {
        if (checked) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  };

  const handleSelectRow = (key, checked) => {
    if (!key || selectionLocked) return;
    const row = compliantRows.find(
      entry =>
        (entry.selectionKey ??
          `${entry.bidder?.id ?? entry.bidder?.idOffer}-${entry.kind}`) === key
    );
    if (!negotiationMode && row && isOfferRowImprovement(row)) return;
    if (negotiationMode && !hasNegotiationOfferId(row?.bidder?.idOffer)) return;
    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  if (!hasData && !isLoadingBidders && !isLoadingDeliveryDetails) {
    return null;
  }

  return (
    <Card
      className={`border shadow-none mb-3${selectionLocked ? ' opacity-75' : ''}`}
    >
      <CardHeader
        className='bg-light border-bottom p-3 d-flex justify-content-between align-items-center cursor-pointer'
        onClick={() => setIsExpanded(prev => !prev)}
        role='button'
        aria-expanded={isExpanded}
        aria-controls={`requirement-body-lpg-${requirementIndex}`}
      >
        <div>
          <h6 className='mb-0 text-dark fw-bold fs-13'>
            Requirement {requirementIndex + 1}
            {selectionLocked ? (
              <Badge color='secondary' className='ms-2 fs-11 fw-medium'>
                Locked
              </Badge>
            ) : null}
          </h6>
          <span className='text-muted fs-12'>{incotermRouteLabel}</span>
          {selectionLocked ? (
            <div className='text-muted fs-11 mt-1'>
              Another requirement is selected for this Delivery Unit.
            </div>
          ) : null}
        </div>
        <i
          className={`fs-20 ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}
        ></i>
      </CardHeader>

      <Collapse isOpen={isExpanded}>
        <CardBody
          id={`requirement-body-lpg-${requirementIndex}`}
          className='p-0'
        >
          <div className='p-3 border-bottom'>
            <Row className='g-3'>
              <Col md={3}>
                <div className='text-muted fw-semibold fs-12 mb-2'>
                  Incoterm - Route
                </div>
                <div className='text-dark fs-13 mt-4'>{incotermRouteLabel}</div>
              </Col>
              <Col md={3}>
                <div className='text-muted fw-semibold fs-12 mb-2'>
                  Operational Tolerances
                </div>
                <div className='text-dark fs-13 mt-4'>
                  <OperationalToleranceSectionValue
                    procurement={procurement}
                    showCounter={false}
                  />
                </div>
              </Col>
              <PortSummary
                label='Port 1'
                port={requirement.port1}
                parcelSizes={parcelSizes}
                dateRange={formatTimelineRange(
                  timeline,
                  1,
                  requirement.aldAdd1
                )}
              />
              <PortSummary
                label='Port 2'
                port={requirement.port2}
                parcelSizes={parcelSizes}
                dateRange={formatTimelineRange(
                  timeline,
                  2,
                  requirement.aldAdd2
                )}
              />
              <PortSummary
                label='Port 3'
                port={requirement.port3}
                parcelSizes={parcelSizes}
                dateRange={formatTimelineRange(
                  timeline,
                  3,
                  requirement.aldAdd3
                )}
              />
            </Row>
          </div>

          <div className='table-responsive m-3 border rounded'>
            <Table
              className='table-borderless align-middle mb-0 w-100 offer-compare-table'
              style={OFFER_TABLE_STYLE}
              aria-label='Offer comparison'
            >
              <thead className='bg-light text-muted fs-12 border-bottom'>
                <tr>
                  {showCheckboxes && (
                    <th
                      scope='col'
                      style={thStyle('check')}
                      className='text-center'
                    >
                      <div className='d-inline-flex flex-column align-items-center gap-1'>
                        <Input
                          innerRef={selectAllRef}
                          type='checkbox'
                          className='form-check-input'
                          checked={allSelected}
                          disabled={
                            selectionLocked ||
                            isLoadingBidders ||
                            isLoadingDeliveryDetails ||
                            !selectableKeys.length
                          }
                          onChange={e => handleSelectAll(e.target.checked)}
                          title='Select all offers in this requirement'
                          aria-label='Select all offers in this requirement'
                        />
                        <span className='fs-10 text-muted lh-1'>All</span>
                      </div>
                    </th>
                  )}
                  {showCheckboxes && !negotiationMode && (
                    <th scope='col' style={thStyle('statusImprovement')}>
                      <ThLabel {...OFFER_HEADER.improvement} />
                    </th>
                  )}
                  <th scope='col' style={thStyle('idOffer')}>
                    <ThLabel {...OFFER_HEADER.idOffer} />
                  </th>
                  <th scope='col' style={thStyle('status')}>
                    <div className='d-flex flex-column gap-1'>
                      <span>Status</span>
                      <OfferStatusFilter
                        value={statusFilters}
                        onChange={setStatusFilters}
                      />
                    </div>
                  </th>
                  <th scope='col' style={thStyle('incoterm')}>
                    <ThLabel {...OFFER_HEADER.incoterm} />
                  </th>
                  <th scope='col' style={thStyle('ot')}>
                    <ThLabel {...OFFER_HEADER.ot} />
                  </th>
                  <th scope='col' style={thStyle('port')}>
                    <ThLabel {...OFFER_HEADER.port1} />
                  </th>
                  <th scope='col' style={thStyle('port')}>
                    <ThLabel {...OFFER_HEADER.port2} />
                  </th>
                  <th scope='col' style={thStyle('port')}>
                    <ThLabel {...OFFER_HEADER.port3} />
                  </th>
                  {requiresCargoRatio && (
                    <th scope='col' style={thStyle('cargoRatio')}>
                      <ThLabel {...OFFER_HEADER.cargoRatio} />
                    </th>
                  )}
                  <th scope='col' style={thStyle('parcelSize')}>
                    <ThLabel {...OFFER_HEADER.parcelSize} />
                  </th>
                  <th scope='col' style={thStyle('alpha')}>
                    <ThLabel {...OFFER_HEADER.alphaMt} />
                  </th>
                  {negotiationMode && (
                    <>
                      <th scope='col' style={thStyle('hps')}>
                        <ThLabel {...OFFER_HEADER.hpsAlpha} />
                      </th>
                      <th scope='col' style={thStyle('matching')}>
                        <ThLabel {...OFFER_HEADER.matchingHps} />
                      </th>
                    </>
                  )}
                  {improvementOnly || negotiationMode ? (
                    usesDyedUndyed ? (
                      <>
                        <th scope='col' style={thStyle('alpha')}>
                          <ThLabel {...OFFER_HEADER.alphaDyedImp} />
                        </th>
                        <th scope='col' style={thStyle('alpha')}>
                          <ThLabel {...OFFER_HEADER.alphaUndyedImp} />
                        </th>
                      </>
                    ) : (
                      <th scope='col' style={thStyle('alpha')}>
                        <ThLabel {...OFFER_HEADER.alphaImp} />
                      </th>
                    )
                  ) : (
                    <th scope='col' style={thStyle('action')}>
                      <ThLabel {...OFFER_HEADER.action} />
                    </th>
                  )}
                  {showImprovementHpsColumns && (
                    <>
                      <th scope='col' style={thStyle('hps')}>
                        <ThLabel {...OFFER_HEADER.hpsAlpha} />
                      </th>
                      <th scope='col' style={thStyle('matching')}>
                        <ThLabel {...OFFER_HEADER.matchingHps} />
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className='fs-13'>
                <OfferDetailRows
                  compliantRows={filteredRows}
                  isLoading={isLoadingBidders || isLoadingDeliveryDetails}
                  selectedRowKeys={selectedRowKeys}
                  onSelectRow={handleSelectRow}
                  requiresCargoRatio={requiresCargoRatio}
                  parcelSizes={parcelSizes}
                  onViewDetail={onViewDetail}
                  improvementOnly={improvementOnly}
                  showCheckboxes={showCheckboxes}
                  selectionLocked={selectionLocked}
                  negotiationMode={negotiationMode}
                  usesDyedUndyed={usesDyedUndyed}
                  colSpan={colSpan}
                  alphaImprovementEditable={alphaImprovementEditable}
                  onAlphaImprovementChange={onAlphaImprovementChange}
                  hpsAvailable={isHpsAvailable(procurement)}
                  procurement={procurement}
                  hpsCatalog={hpsCatalog}
                  showImprovementHpsColumns={showImprovementHpsColumns}
                  isImprovementClosed={isImprovementClosed}
                />
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Collapse>
    </Card>
  );
};

const ParcelOfferCard = ({
  parcel,
  procurement,
  bidders,
  isLoadingBidders,
  deliveryDetails,
  isLoadingDeliveryDetails,
  requiresCargoRatio,
  onViewDetail,
  improvementOnly = false,
  negotiationMode = false,
  showCheckboxes = false,
  focusRequirementIndex,
  onRequirementSelectionChange,
  selectionClearToken = 0,
  selectAllToken = 0,
  alphaImprovementEditable = false,
  onAlphaImprovementChange,
  onNegotiationOfferAvailability,
  preselectedOffers = null,
  showImprovementHpsColumns = false,
  isImprovementClosed = false,
}) => {
  const requirements = useMemo(
    () => buildLpgRequirementsFromParcel(parcel),
    [parcel]
  );
  const parcelId = parcel?.id ?? parcel?.quantityDeliveryTermId ?? null;
  const [activeNegotiationKey, setActiveNegotiationKey] = useState(null);
  const parcelDeliveryDetails = useMemo(
    () =>
      parcelId
        ? deliveryDetails.filter(
            row => String(row.quantityDeliveryTermId) === String(parcelId)
          )
        : deliveryDetails,
    [deliveryDetails, parcelId]
  );

  useEffect(() => {
    if (!selectionClearToken) return;
    setActiveNegotiationKey(null);
  }, [selectionClearToken]);

  const handleRequirementSelectionChange = useCallback(
    (selectionKey, entries) => {
      if (negotiationMode) {
        if (entries?.length > 0) {
          setActiveNegotiationKey(selectionKey);
        } else if (activeNegotiationKey === selectionKey) {
          setActiveNegotiationKey(null);
        }
      }
      onRequirementSelectionChange?.(selectionKey, entries);
    },
    [negotiationMode, activeNegotiationKey, onRequirementSelectionChange]
  );

  const deliveryContent = (
    <CardBody className='p-4'>
      <Row className='mb-4'>
        <Col md={4} className='border-end'>
          <div className='text-dark fw-semibold fs-13 mb-1'>Product Name</div>
          <div className='text-muted fs-13'>{getProductLabel(procurement)}</div>
        </Col>
        <Col md={4} className='border-end'>
          <div className='text-dark fw-semibold fs-13 mb-1'>Quantity</div>
          <div className='text-muted fs-13'>{getQuantityLabel(parcel)}</div>
        </Col>
        <Col md={4}>
          <div className='text-dark fw-semibold fs-13 mb-1'>
            Operation Tolerance
          </div>
          <div className='text-muted fs-13'>
            <OperationalToleranceSectionValue
              procurement={procurement}
              showCounter={false}
            />
          </div>
        </Col>
      </Row>

      {requirements.map((requirement, index) => {
        if (
          focusRequirementIndex != null &&
          Number(focusRequirementIndex) !== Number(index)
        ) {
          return null;
        }
        const selectionKey = `${parcelId || 'parcel'}-req-${index}`;
        const selectionLocked =
          negotiationMode &&
          focusRequirementIndex == null &&
          Boolean(activeNegotiationKey) &&
          activeNegotiationKey !== selectionKey;
        return (
          <RequirementCard
            key={`${parcel?.id || 'parcel'}-req-${index}`}
            selectionKey={selectionKey}
            requirement={requirement}
            requirementIndex={index}
            parcel={parcel}
            bidders={bidders}
            isLoadingBidders={isLoadingBidders}
            parcelId={parcelId}
            deliveryDetails={parcelDeliveryDetails}
            isLoadingDeliveryDetails={isLoadingDeliveryDetails}
            procurement={procurement}
            requiresCargoRatio={requiresCargoRatio}
            onViewDetail={onViewDetail}
            improvementOnly={improvementOnly}
            negotiationMode={negotiationMode}
            showCheckboxes={showCheckboxes}
            selectionLocked={selectionLocked}
            onRequirementSelectionChange={handleRequirementSelectionChange}
            selectionClearToken={selectionClearToken}
            selectAllToken={selectAllToken}
            alphaImprovementEditable={alphaImprovementEditable}
            onAlphaImprovementChange={onAlphaImprovementChange}
            onNegotiationOfferAvailability={onNegotiationOfferAvailability}
            preselectedOffers={preselectedOffers}
            showImprovementHpsColumns={showImprovementHpsColumns}
            isImprovementClosed={isImprovementClosed}
          />
        );
      })}
    </CardBody>
  );

  return (
    <Card className='border shadow-none mb-4'>
      <CardHeader className='bg-light border-bottom p-3'>
        <h6 className='mb-0 text-dark fw-bold fs-14'>
          Delivery Unit : {displayValue(parcel?.deliveryUnit, '-')}
        </h6>
      </CardHeader>
      <ParcelDeliveryPricingTabs
        parcel={parcel}
        deliveryContent={deliveryContent}
      />
    </Card>
  );
};

const OfferTableLpg = ({
  procurementId,
  procurement,
  parcels = [],
  isLoadingParcels = false,
  onSelectedOffersChange,
  selectionClearToken = 0,
  viewMode = 'offer',
  selectionDisabled = false,
  alphaImprovementEditable = false,
  onAlphaImprovementChange,
  onImprovementMeta,
  focusParcelId,
  focusRequirementIndex,
  isActive = true,
  onNegotiationOfferAvailability,
  // TE Negotiation prefill: saved partners to pre-check.
  preselectedOffers = null,
  // IC (mitra): tampilkan hanya bidder milik mitra login.
  bidderId = null,
}) => {
  const improvementOnly = viewMode === 'improvement';
  const negotiationMode = viewMode === 'negotiation';
  // HPS is evaluator-only; IC mitra view always passes bidderId.
  const showImprovementHpsColumns = improvementOnly && bidderId == null;
  const showCheckboxes =
    negotiationMode || (viewMode === 'offer' && !selectionDisabled);
  const filterImprovement = improvementOnly || negotiationMode;
  const { items: allBidders, isLoading: isLoadingBidders } = useSubmittedBidder(
    procurementId,
    {
      isActive,
      refreshToken: selectionClearToken,
      // Negotiation needs every bidder, not the first page of 20.
      pageSize: negotiationMode ? 500 : undefined,
    }
  );
  const bidders = useMemo(
    () =>
      bidderId
        ? (allBidders ?? []).filter(
            bidder => String(bidder?.id) === String(bidderId)
          )
        : allBidders,
    [allBidders, bidderId]
  );

  // Cargo ratio follows the product type of each cargo (quantity delivery
  // term); terms without a parcel fall back to the procurement's product type.
  const requiresCargoRatioByTermId = useMemo(() => {
    const map = {};
    parcels.forEach(parcel => {
      if (parcel?.id) {
        map[parcel.id] = requiresLpgCargoRatioForCargo(procurement, parcel);
      }
    });
    return map;
  }, [parcels, procurement]);
  const requiresCargoRatioFor = useCallback(
    termId =>
      requiresCargoRatioByTermId[termId] ??
      requiresLpgCargoRatioForCargo(procurement, null),
    [requiresCargoRatioByTermId, procurement]
  );

  const baseDeliveryDetails = useMemo(
    () => flattenSubmittedBidderDeliveryDetails(bidders),
    [bidders]
  );

  const [deliveryDetails, setDeliveryDetails] = useState([]);
  const [isEnriching, setIsEnriching] = useState(false);
  const [hasEnrichmentSettled, setHasEnrichmentSettled] = useState(false);
  const improvementClosingDate = improvementOnly
    ? resolveImprovementClosingDate(deliveryDetails)
    : null;
  const isImprovementClosed =
    improvementOnly &&
    Boolean(improvementClosingDate) &&
    !isImprovementClosingActive(improvementClosingDate);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectionByRequirement, setSelectionByRequirement] = useState({});
  const [selectAllToken, setSelectAllToken] = useState(0);
  const [localClearToken, setLocalClearToken] = useState(0);
  const mergedClearToken = selectionClearToken + localClearToken;
  const selectedCount = useMemo(
    () =>
      Object.values(selectionByRequirement).reduce(
        (total, entries) => total + (entries?.length || 0),
        0
      ),
    [selectionByRequirement]
  );

  const handleRequirementSelectionChange = useCallback(
    (selectionKey, entries) => {
      setSelectionByRequirement(prev => {
        const prevEntries = prev[selectionKey] || [];
        if (
          prevEntries.length === entries.length &&
          prevEntries.every(
            (entry, index) =>
              entry.quantityIncotermId === entries[index]?.quantityIncotermId &&
              entry.kind === entries[index]?.kind
          )
        ) {
          return prev;
        }
        return { ...prev, [selectionKey]: entries };
      });
    },
    []
  );

  useEffect(() => {
    if (!showCheckboxes || !onSelectedOffersChange) return;
    onSelectedOffersChange?.(
      Object.values(selectionByRequirement).flatMap(entries => entries || [])
    );
  }, [selectionByRequirement, onSelectedOffersChange, showCheckboxes]);

  useEffect(() => {
    if (improvementOnly || !mergedClearToken) return;
    setSelectionByRequirement({});
  }, [mergedClearToken, improvementOnly]);

  useEffect(() => {
    let cancelled = false;

    const enrichDetails = async () => {
      setHasEnrichmentSettled(false);
      if ((improvementOnly || negotiationMode) && onImprovementMeta) {
        onImprovementMeta({ ready: false });
      }

      if (isLoadingBidders) {
        return;
      }

      if (!baseDeliveryDetails.length) {
        if (!cancelled) {
          setDeliveryDetails([]);
          setIsEnriching(false);
          setHasEnrichmentSettled(true);
        }
        return;
      }

      const token = getAuthToken();
      if (!token || !procurementId) {
        if (!cancelled) {
          setDeliveryDetails(baseDeliveryDetails);
          setIsEnriching(false);
          setHasEnrichmentSettled(true);
        }
        return;
      }

      setIsEnriching(true);

      try {
        const byTermId = new Map();
        baseDeliveryDetails.forEach(row => {
          const termId = row.quantityDeliveryTermId ?? 'default';
          if (!byTermId.has(termId)) byTermId.set(termId, []);
          byTermId.get(termId).push(row);
        });

        const enriched = [];
        // Cache request per siklus enrich (key URL / master), dibuang tiap re-run.
        const requestCache = new Map();
        for (const [termId, items] of byTermId.entries()) {
          let nextItems = items;

          const hasCargoRatio = nextItems.some(
            item => item.cargoRatio || item.cargoRatioLabel
          );

          const termRequiresCargoRatio = requiresCargoRatioFor(termId);

          if (
            termId !== 'default' &&
            termRequiresCargoRatio &&
            !hasCargoRatio
          ) {
            try {
              // be#456: Alpha cargo ratio per bidder (ProcurementMitraId).
              nextItems = await enrichRowsPerBidder(
                nextItems,
                (bidderItems, procurementMitraId) =>
                  enrichLpgDeliveryItemsWithCargoRatio({
                    procurementId,
                    quantityDeliveryTermId: termId,
                    items: bidderItems,
                    skipCargoRatio: false,
                    procurementMitraId,
                    cache: requestCache,
                  })
              );
            } catch (error) {
              // keep base items when cargo-ratio enrichment fails
            }
          } else if (termId !== 'default' && !termRequiresCargoRatio) {
            try {
              nextItems = await enrichLpgDeliveryItemsWithCargoRatio({
                procurementId,
                quantityDeliveryTermId: termId,
                items: nextItems,
                skipCargoRatio: true,
                cache: requestCache,
              });
            } catch (error) {
              // keep base items when parcel-size enrichment fails
            }
          }

          if (termId !== 'default') {
            try {
              const bidderMap = Object.fromEntries(
                (bidders || []).map(bidder => [String(bidder.id), bidder])
              );
              // be#456: satu get-delivery-detail per bidder (nilai offer per mitra).
              nextItems = await enrichRowsPerBidder(
                nextItems,
                (bidderItems, procurementMitraId) =>
                  mergeBidderDeliveryDetail({
                    procurementId,
                    termId,
                    token,
                    items: bidderItems,
                    procurementMitraId,
                    bidderMap,
                    cache: requestCache,
                  })
              );
            } catch (error) {
              // keep submitted-bidder rows when delivery-detail merge fails
            }
            try {
              nextItems = await enrichDeliveryItemsWithFobLocations({
                quantityDeliveryTermId: termId,
                token,
                items: nextItems,
              });
            } catch (error) {
              // keep items when FOB enrichment fails
            }
            try {
              nextItems = await enrichDeliveryItemsWithCounterOffers({
                quantityDeliveryTermId: termId,
                token,
                items: nextItems,
                requireIncotermCounter: true,
                matchCounterOwner: negotiationMode,
              });
            } catch (error) {
              // keep items when counter enrichment fails
            }
          }

          enriched.push(...nextItems);
        }

        if (!cancelled) setDeliveryDetails(enriched);
      } finally {
        if (!cancelled) {
          setIsEnriching(false);
          setHasEnrichmentSettled(true);
        }
      }
    };

    enrichDetails();

    return () => {
      cancelled = true;
    };
  }, [
    baseDeliveryDetails,
    procurementId,
    requiresCargoRatioFor,
    selectionClearToken,
    bidders,
    isLoadingBidders,
    improvementOnly,
    negotiationMode,
    onImprovementMeta,
  ]);

  useEffect(() => {
    if (!(improvementOnly || negotiationMode) || !onImprovementMeta) return;
    if (isLoadingBidders || !hasEnrichmentSettled || isEnriching) return;
    onImprovementMeta({ deliveryDetails, ready: true });
  }, [
    deliveryDetails,
    improvementOnly,
    negotiationMode,
    onImprovementMeta,
    hasEnrichmentSettled,
    isEnriching,
    isLoadingBidders,
  ]);

  const handleViewOfferDetail = (bidder, detailRow) => {
    if (!bidder?.id) return;
    setSelectedOffer({ bidder, detailRow });
    setIsDetailModalOpen(true);
  };

  const toggleDetailModal = () => {
    setIsDetailModalOpen(prev => !prev);
    if (isDetailModalOpen) {
      setSelectedOffer(null);
    }
  };

  const hasImprovementData = useMemo(() => {
    if (!filterImprovement) return true;
    const parcelItems = parcels.length
      ? parcels
      : [{ id: 'default', deliveryUnit: '-' }];
    return parcelItems.some(parcel => {
      const parcelId = parcel?.id ?? parcel?.quantityDeliveryTermId ?? null;
      const parcelDeliveryDetails = parcelId
        ? deliveryDetails.filter(
            row => String(row.quantityDeliveryTermId) === String(parcelId)
          )
        : deliveryDetails;
      return buildLpgRequirementsFromParcel(parcel).some((requirement, index) =>
        requirementHasOfferData({
          requirement,
          requirementIndex: index,
          parcelId,
          deliveryDetails: parcelDeliveryDetails,
          bidders,
          improvementOnly: true,
        })
      );
    });
  }, [filterImprovement, parcels, deliveryDetails, bidders]);

  if (isLoadingParcels || isLoadingBidders) {
    return <SkeletonTable rows={5} columns={8} />;
  }

  if (filterImprovement && (!hasEnrichmentSettled || isEnriching)) {
    return <SkeletonTable rows={5} columns={8} />;
  }

  if (!bidders.length) {
    return (
      <div className='text-center text-muted py-4 fs-13'>
        {filterImprovement
          ? 'Belum ada data improvement.'
          : 'Belum ada penawaran yang disubmit.'}
      </div>
    );
  }

  if (filterImprovement && !hasImprovementData) {
    return (
      <div className='text-center text-muted py-4 fs-13'>
        Belum ada data improvement.
      </div>
    );
  }

  const parcelItems = (
    parcels.length ? parcels : [{ id: 'default', deliveryUnit: '-' }]
  ).filter(parcel => {
    if (focusParcelId == null || focusParcelId === '') return true;
    const id = parcel?.id ?? parcel?.quantityDeliveryTermId ?? null;
    return String(id) === String(focusParcelId);
  });

  return (
    <>
      {showCheckboxes && !negotiationMode ? (
        <OfferSelectAllBar
          selectedCount={selectedCount}
          disabled={isLoadingBidders || isEnriching || isLoadingParcels}
          onSelectAll={() => setSelectAllToken(token => token + 1)}
          onClear={() => setLocalClearToken(token => token + 1)}
        />
      ) : null}
      {parcelItems.map(parcel => (
        <ParcelOfferCard
          key={parcel.id || parcel.deliveryUnit}
          parcel={parcel}
          procurement={procurement}
          bidders={bidders}
          isLoadingBidders={isLoadingBidders}
          deliveryDetails={deliveryDetails}
          isLoadingDeliveryDetails={isEnriching}
          requiresCargoRatio={requiresCargoRatioFor(parcel.id)}
          onViewDetail={handleViewOfferDetail}
          improvementOnly={improvementOnly}
          negotiationMode={negotiationMode}
          showCheckboxes={showCheckboxes}
          focusRequirementIndex={focusRequirementIndex}
          onRequirementSelectionChange={
            showCheckboxes ? handleRequirementSelectionChange : undefined
          }
          selectionClearToken={showCheckboxes ? mergedClearToken : 0}
          selectAllToken={
            showCheckboxes && !negotiationMode ? selectAllToken : 0
          }
          alphaImprovementEditable={alphaImprovementEditable}
          onAlphaImprovementChange={onAlphaImprovementChange}
          onNegotiationOfferAvailability={onNegotiationOfferAvailability}
          preselectedOffers={preselectedOffers}
          showImprovementHpsColumns={showImprovementHpsColumns}
          isImprovementClosed={isImprovementClosed}
        />
      ))}
      {!improvementOnly && (
        <ModalOffer
          isOpen={isDetailModalOpen}
          toggle={toggleDetailModal}
          bidder={selectedOffer?.bidder}
          procurement={procurement}
        />
      )}
    </>
  );
};

export default OfferTableLpg;
